import { DevMode, useDeveloperModeEnabled } from '@chipinside/devmode'
import {
  InputCheckbox,
  InputRadio,
  InputSelectASync,
  InputSelectSearchable,
  InputSelectSync,
  Moment,
} from '@chipinside/frontend'
import { PageBuilder } from '@chipinside/pagebuilder'
import { isEmpty, isFunction, max, min, omit, omitBy } from 'lodash'
import React from 'react'

import { FormBuilderColor } from '#/components/FormCall/fields/Color'
import { List } from '#/components/FormCall/fields/list'
import { FormBuilderMessage } from '#/components/FormCall/fields/message'
import { FormBuilderNumber } from '#/components/FormCall/fields/number'
import { FormBuilderSession } from '#/components/FormCall/fields/session'
import { Sortable } from '#/components/FormCall/fields/sortable'
import { DataGridWrapper } from '#/components/FormCall/fields/Wrappers/datagrid'
import DateTimePicker from '#/components/FormCall/Inputs/DateTimePicker'
import EmailInput from '#/components/FormCall/Inputs/EmailInput'
import { FileField } from '#/components/FormCall/Inputs/InputFile'
import InputImage from '#/components/FormCall/Inputs/InputImage'
import InputMask from '#/components/FormCall/Inputs/InputMask'
import InputPhone from '#/components/FormCall/Inputs/InputPhone'
import InputSwitch from '#/components/FormCall/Inputs/InputSwitch'
import InputText from '#/components/FormCall/Inputs/InputText'
import PinMapInput from '#/components/FormCall/Inputs/PinMap'
import TextEditor from '#/components/FormCall/Inputs/TextEditor'
import TimePickerInput from '#/components/FormCall/Inputs/TimePicker'

function getMinFormattedDate(minDate, isAfter) {
  return isAfter
    ? Moment(minDate).add(1, 'day').format('YYYY-MM-DD')
    : Moment(minDate).format('YYYY-MM-DD')
}

function getMaxFormattedDate(maxDate, isBefore) {
  return isBefore
    ? Moment(maxDate).subtract(1, 'day').format('YYYY-MM-DD')
    : Moment(maxDate).format('YYYY-MM-DD')
}

function getMinMaxDate(props) {
  const isBefore = !!props?.rules?.before?.value
  const isAfter = !!props?.rules?.after?.value

  const maxDate = min([props?.rules?.before_or_equal?.value, props?.rules?.before?.value])

  const minDate = max([props?.rules?.after_or_equal?.value, props?.rules?.after?.value])

  if (props.type === 'date') {
    return {
      maxDate: maxDate ? getMaxFormattedDate(maxDate, isBefore) : null,
      minDate: minDate ? getMinFormattedDate(minDate, isAfter) : null,
    }
  }

  return {
    maxDate: min([props?.rules?.before_or_equal?.value, props?.rules?.before?.value]),
    minDate: max([props?.rules?.after_or_equal?.value, props?.rules?.after?.value]),
  }
}

const FormBuilderWrapperSelect = props => {
  function handleChange(options) {
    if (isEmpty(options)) {
      return props.onChange(options)
    }

    if (props.multi) {
      return props.onChange(options.map(options => options.value))
    }

    return props.onChange(options.value)
  }

  // Ensure controlled component by providing null as default value
  const value = props.value ?? null
  const controlledProps = { ...props, onChange: handleChange, value }

  if (props.searchable) {
    return <InputSelectSearchable {...controlledProps} />
  }
  if (props.source) {
    return <InputSelectASync {...controlledProps} />
  }
  return <InputSelectSync {...controlledProps} />
}
/**
 * Retorna as propriedades do componente para o DevTool, removendo funções e componentes que não são serializáveis.
 */
const getDevToolProps = props => {
  // Remove funções e componentes do objeto props, pois não são serializáveis
  const cleanedProps = omitBy(props, value => isFunction(value) || React.isValidElement(value))

  // Remove propriedades que não são serializáveis
  const cleanedPropsWithoutIgnored = omit(cleanedProps, ['form'])

  try {
    JSON.stringify(cleanedPropsWithoutIgnored)
    return cleanedPropsWithoutIgnored
  } catch {
    return null
  }
}

/**
 * Retorna um componente que envolve o campo do formulário com o DevTool, caso o modo de desenvolvedor esteja habilitado.
 */
const DevToolWrapper = ({ children, ...props }) => {
  const devMode = useDeveloperModeEnabled()

  if (!devMode) {
    return children
  }

  const cleanedProps = getDevToolProps(props)

  if (!cleanedProps) {
    return children
  }

  return (
    <DevMode
      code={cleanedProps}
      sx={{
        '& > :last-child': { width: '100%' },
      }}
    >
      {children}
    </DevMode>
  )
}

/* prettier-ignore */
const Fields = {
  'boolean':      props =>  <InputSwitch                {...props} />,
  'checkbox':     props =>  <InputCheckbox              {...props} />,
  'choice':       props =>  <InputRadio                 {...props} />,
  'color':        props =>  <FormBuilderColor           {...props} />,
  'datagrid':     props =>  <DataGridWrapper            {...props} />,
  'date':         props =>  <DateTimePicker             {...props} {...getMinMaxDate(props)}/>,
  'email':        props =>  <InputText                  {...props} />,
  'email-list':   props =>  <EmailInput                 {...props} />,
  'file':         props =>  <FileField                  {...props} />,
  'image':        props =>  <InputImage                 {...props} />,
  'integer':      props =>  <FormBuilderNumber          {...props} />,
  "list":         props =>  <List                       {...props} />,
  'mask':         props =>  <InputMask                  {...props} />,
  'message':      props =>  <FormBuilderMessage         {...props} />,
  'number':       props =>  <FormBuilderNumber          {...props} />,
  'pagebuilder':  props =>  <PageBuilder                {...props} />,
  'password':     props =>  <InputText                  {...props} />,
  'percentage':   props =>  <FormBuilderNumber          {...props} />,
  'pin':          props =>  <PinMapInput                {...props} />,
  'select':       props =>  <FormBuilderWrapperSelect   {...props} />,
  'separator':    () =>     <br />,
  'session':      props =>  <FormBuilderSession         {...props} />,
  'sortable':     props =>  <Sortable                   {...props} />,
  'tel':          props =>  <InputPhone                 {...props} />,
  'text':         props =>  <InputText                  {...props} />,
  'textarea':     props =>  <InputText                  {...props} multiline rows={3} />,
  'time':         props =>  <TimePickerInput            {...props} />,
  'timestamp':    props =>  <DateTimePicker             {...props} {...getMinMaxDate(props)} />,
  'wysiwyg':      props =>  <TextEditor                 {...props} />
}

const FormBuilderFields = Object.fromEntries(
  Object.entries(Fields).map(([name, Field]) => [
    name,
    props => {
      if (['session', 'separator'].includes(name)) {
        return <Field {...props} />
      }

      return (
        <DevToolWrapper {...props}>
          <Field {...props} />
        </DevToolWrapper>
      )
    },
  ]),
)

export { FormBuilderFields }
